import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { ProportionalityGate } from "@/components/ProportionalityGate";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MagnifyingGlass, Buildings, User, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function NewQuery() {
  const navigate = useNavigate();
  const [type, setType] = useState("corporate");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [allResults, setAllResults] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [otherMin, setOtherMin] = useState(30);
  const [gateOpen, setGateOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/business-purposes").then((r) => {
      setPurposes(r.data.purposes);
      setOtherMin(r.data.other_min_chars);
    });
  }, []);

  const doSearch = useCallback(async () => {
    const { data } = await api.get("/entities/search", { params: { q, type } });
    setResults(data.results);
  }, [q, type]);

  useEffect(() => {
    doSearch();
  }, [doSearch]);

  // All demo subjects of the current type (used as fallback suggestions).
  useEffect(() => {
    api.get("/entities/search", { params: { q: "", type } }).then(({ data }) => setAllResults(data.results));
  }, [type]);

  const openGate = (item) => {
    setTarget(item);
    setGateOpen(true);
  };

  const runSearch = async (purposeCode, justification) => {
    setSubmitting(true);
    try {
      const endpoint = type === "corporate" ? "/search/corporate" : "/search/individual";
      const { data } = await api.post(endpoint, {
        target_id: target.id,
        business_purpose_code: purposeCode,
        justification_text: justification,
      });
      toast.success("Report generato e registrato nel ledger");
      setGateOpen(false);
      navigate(`/app/report/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore durante la ricerca");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell breadcrumb="Nuova indagine">
      <div className="max-w-3xl">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-zinc-950">Nuova indagine di due diligence</h1>
        <p className="text-sm text-zinc-500 mt-1 mb-6">
          Seleziona il soggetto. Prima dell'esecuzione dovrai documentare la finalità legittima (Proportionality Gate).
        </p>

        <div className="bg-white border border-zinc-950 rounded-sm p-5 shadow-sm">
          <Tabs value={type} onValueChange={(v) => { setType(v); setResults([]); }}>
            <TabsList className="bg-zinc-100 rounded-sm">
              <TabsTrigger value="corporate" data-testid="tab-corporate" className="rounded-sm data-[state=active]:bg-zinc-950 data-[state=active]:text-white gap-2">
                <Buildings size={15} weight="duotone" /> Società
              </TabsTrigger>
              <TabsTrigger value="individual" data-testid="tab-individual" className="rounded-sm data-[state=active]:bg-zinc-950 data-[state=active]:text-white gap-2">
                <User size={15} weight="duotone" /> Persona
              </TabsTrigger>
            </TabsList>
          </Tabs>

          <div className="relative mt-4">
            <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={type === "corporate" ? "Cerca per denominazione (es. Lombardi, Adriatica…)" : "Cerca per nome (es. Marco Lombardi…)"}
              className="pl-10 border-zinc-300 rounded-sm h-11"
            />
          </div>
          <p className="text-[11px] text-zinc-400 font-mono mt-2 uppercase tracking-wide">
            Dati dimostrativi (MVP) · Fonti: InfoCamere · Gazzetta Ufficiale · Tribunali
          </p>
          <p className="text-xs text-zinc-500 mt-2" data-testid="search-help">
            Questo MVP usa un set di dati dimostrativo con {allResults.length}{" "}
            {type === "corporate" ? "società" : "persone"} disponibili. Lascia il campo vuoto per vederle tutte.
          </p>
        </div>

        <div className="mt-4 space-y-2" data-testid="search-results">
          {(() => {
            const noMatch = q.trim().length > 0 && results.length === 0;
            const list = noMatch ? allResults : results;
            return (
              <>
                {noMatch && (
                  <div data-testid="search-no-match" className="text-sm text-zinc-600 border border-amber-200 bg-amber-50 rounded-sm px-4 py-3">
                    Nessuna corrispondenza per <span className="font-medium">“{q}”</span>. Questi sono i soggetti dimostrativi disponibili — selezionane uno per procedere:
                  </div>
                )}
                {list.length === 0 ? (
                  <div className="text-sm text-zinc-500 border border-dashed border-zinc-300 rounded-sm p-6 text-center">
                    Nessun soggetto dimostrativo disponibile.
                  </div>
                ) : (
                  list.map((item) => (
                    <button
                      key={item.id}
                      data-testid={`result-${item.id}`}
                      onClick={() => openGate(item)}
                      className="w-full flex items-center gap-4 bg-white border border-zinc-200 hover:border-zinc-950 rounded-sm px-4 py-3 text-left transition-colors duration-150 group"
                    >
                      <div className="w-9 h-9 rounded-sm bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
                        {item.type === "corporate" ? <Buildings size={17} className="text-zinc-600" /> : <User size={17} className="text-zinc-600" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-zinc-900 truncate">{item.label}</div>
                        <div className="text-xs text-zinc-500 truncate">{item.sub}</div>
                      </div>
                      <span className="text-[10px] font-mono text-zinc-400">{item.id}</span>
                      <CaretRight size={16} className="text-zinc-300 group-hover:text-zinc-950 transition-colors duration-150" />
                    </button>
                  ))
                )}
              </>
            );
          })()}
        </div>
      </div>

      <ProportionalityGate
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        target={target}
        purposes={purposes}
        otherMin={otherMin}
        onConfirm={runSearch}
        submitting={submitting}
      />
    </AppShell>
  );
}
